import { useMemo } from 'react';

import { useAppSelector } from '../store/hooks';

import type { User } from '../types';

// Simple permission checker based on backend API response
export function hasPermission(user: User | null, permission: string): boolean {
  if (!user?.permissions) {
    return false;
  }

  const hasIt = user.permissions.some(p => p.permission === permission);
  return hasIt;
}

export function hasRole(user: User | null, role: string): boolean {
  if (!user) return false;
  return user.role === role;
}

export function usePermissions() {
  const { user } = useAppSelector(state => state.auth);

  const permissions = useMemo(() => {
    if (!user?.permissions) return [];
    return user.permissions.map(p => p.permission);
  }, [user]);

  const permissionChecker = useMemo(() => {
    return {
      has: (permission: string) => hasPermission(user, permission),
    };
  }, [user]);

  const isTeacher = useMemo(() => hasRole(user, 'teacher'), [user]);
  const isEvaluator = useMemo(() => hasRole(user, 'evaluator'), [user]);
  const isSchoolAdmin = useMemo(
    () =>
      ['school_admin', 'principal', 'assistant_principal'].some(role =>
        hasRole(user, role)
      ),
    [user]
  );
  const isDistrictAdmin = useMemo(
    () => hasRole(user, 'district_admin'),
    [user]
  );
  const isSuperAdmin = useMemo(() => hasRole(user, 'super_admin'), [user]);

  return {
    user,
    permissions,
    hasRole: (role: string) => hasRole(user, role),
    permissionChecker,
    isTeacher,
    isEvaluator,
    isSchoolAdmin,
    isDistrictAdmin,
    isSuperAdmin,
    hasPermission: (permission: string) => hasPermission(user, permission),
  };
}

export default usePermissions;
